/* eslint-disable jsx-a11y/no-autofocus */
import React from "react"
import { useField } from "formik"
import { observer } from "mobx-react-lite"
import clsx from "clsx"

import Icon from "@components/ui/Icon/Icon"
import { useId } from "@components/hooks/useId"
import Text from "@components/ui/Typography/Text"
import { ModalsTypes } from "@components/modals/constants"
import useModal from "@components/modals/useModal"
import { useStore } from "@store"
import Chip from "@components/ui/Chip/Chip"

import styles from "./SearchInputField.module.sass"

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string
}

export const SearchInputField: React.FC<Props> = observer(
  ({ id, name, className, autoFocus, disabled, ...rest }) => {
    const inputRef = React.useRef<HTMLInputElement>(null)

    const [filed] = useField<string>(name)

    const htmlId = useId("input_")

    const {
      solutionsStore: { appliedFilters },
      restrictionsStore: access,
    } = useStore()

    const searchFilterModal = useModal(ModalsTypes.SEARCH_FILTERS_MODAL)
    const { hideModal } = useModal(ModalsTypes.SEARCH_FILTERS_MODAL)

    const showFilterModal = () => {
      searchFilterModal.showModal({})
    }

    React.useEffect(() => {
      const handleUrlChange = () => {
        hideModal()
      }

      window.addEventListener("popstate", handleUrlChange)
      window.addEventListener("pushState", handleUrlChange)
      window.addEventListener("replaceState", handleUrlChange)

      return () => {
        window.removeEventListener("popstate", handleUrlChange)
        window.removeEventListener("pushState", handleUrlChange)
        window.removeEventListener("replaceState", handleUrlChange)
      }
    }, [searchFilterModal])

    React.useEffect(() => {
      if (!disabled && autoFocus) setTimeout(() => inputRef.current?.focus(), 0)
    }, [disabled])

    return (
      <>
        {access.isSearchFilterAvailable ? (
          <div
            className={styles.filtersWrapper}
            tabIndex={0}
            role="button"
            onKeyPress={() => {}}
            onClick={showFilterModal}
          >
            <div className={styles.filters}>
              <Icon name="funnel" />
              {!!appliedFilters.length && (
                <Chip
                  color="primary-solid"
                  variant="rounded"
                  className={styles.filterCountBadge}
                >
                  {appliedFilters.length}
                </Chip>
              )}
              <Text>Filters</Text>
            </div>

            <div className={styles.filtersInfo}>
              <Text variant="h6">
                {appliedFilters.length
                  ? `${appliedFilters.length} filters applied`
                  : "All Products & Data"}
              </Text>
              <Icon name="line-arrow-down" />
            </div>
          </div>
        ) : null}

        <label htmlFor={id ?? htmlId} className={styles.input}>
          <input
            autoComplete="off"
            {...rest}
            name={name}
            autoFocus={autoFocus}
            ref={inputRef}
            className={clsx(styles.inputField, className)}
            onChange={filed.onChange}
            value={filed.value}
            id={id ?? htmlId}
          />
        </label>
      </>
    )
  }
)

export default SearchInputField
